// apps/web/src/components/painel/home/saude-sistema.tsx — Fase 17.8, Plano 21 (D-03; UI-SPEC U-22 "Saúde do sistema", Estados "sonda").
// Faixa "Saúde do sistema" do Admin na aba Diretoria: painel de largura total abaixo dos dois painéis (margem de 24px), título e link
// "Administração" (quando a sessão abre a rota) e 4 itens (Omie, Usuários ativos agora, Captação de NF de entrada, Perfis e permissões).
// Cada item: LED de 8px (normal em --tag-faturar com halo, atenção em --color-warning com halo, neutro em --color-muted-foreground) +
// nome (Body 600) + texto de status (Label muted): a cor NUNCA é o único sinal. Item sem fonte mostra o LED neutro e "Sem informação
// agora" sem afetar os outros; carregando é o esqueleto com a altura final. Sem `saude` (sem users:manage) nada é renderizado.
import { useId, type ReactElement } from 'react';
import type { SaudeSistema as DadosSaude } from '@pieltec/contracts';
import { podeAbrirRota } from '../../../lib/painel/blocos';
import { descreverItensDaSaude } from '../../../lib/painel/home/saude';
import { RegiaoCarregando } from './regiao-estado';
import '../../../styles/painel-home.css';

const HREF_ADMINISTRACAO = '/admin/users';

/** Altura final da faixa (cabeçalho + uma linha de itens), para o esqueleto não deslocar o layout. */
export const ALTURA_FAIXA_SAUDE = 120;

export interface SaudeSistemaProps {
  /** Ausente quando a API omitiu o campo (sem `users:manage`): nada é renderizado. */
  readonly saude?: DadosSaude;
  readonly permissoes: readonly string[];
  /** Referência do "há quanto tempo" (o instante dos próprios dados). */
  readonly agora: Date;
  /** Esqueleto da faixa enquanto a primeira carga está em andamento (só para quem tem `users:manage`). */
  readonly carregando?: boolean;
}

function Cabecalho({ idTitulo, permissoes }: { readonly idTitulo: string; readonly permissoes: readonly string[] }): ReactElement {
  return (
    <header className="painel-lista__cabecalho">
      <h2 id={idTitulo} className="painel-lista__titulo">
        <span className="painel-lista__marcador" aria-hidden="true" />
        Saúde do sistema
      </h2>
      {podeAbrirRota(HREF_ADMINISTRACAO, permissoes) && (
        <a className="painel-saude__link" href={HREF_ADMINISTRACAO}>
          Administração
        </a>
      )}
    </header>
  );
}

export function SaudeSistema({ saude, permissoes, agora, carregando = false }: SaudeSistemaProps): ReactElement | null {
  const idTitulo = useId();
  if (saude === undefined) {
    if (!carregando) return null;
    return (
      <section className="painel-saude" data-testid="painel-saude-sistema" aria-labelledby={idTitulo} aria-busy="true">
        <Cabecalho idTitulo={idTitulo} permissoes={permissoes} />
        <RegiaoCarregando id="saude" altura={ALTURA_FAIXA_SAUDE - 56} />
      </section>
    );
  }
  return (
    <section className="painel-saude" data-testid="painel-saude-sistema" aria-labelledby={idTitulo}>
      <Cabecalho idTitulo={idTitulo} permissoes={permissoes} />
      <ul className="painel-saude__itens">
        {descreverItensDaSaude(saude, agora).map((item) => (
          <li key={item.id} className="painel-saude__item" data-testid={`painel-saude-item-${item.id}`} data-led={item.led}>
            <span className="painel-saude__led" aria-hidden="true" />
            <span className="painel-saude__texto">
              <span className="painel-saude__nome">{item.nome}</span>
              <span className="painel-saude__status">{item.texto}</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
